import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchWorkspaceQueryLogMetrics, type QueryLogMetricPoint, type QueryLogMetrics, type QueryLogQuery } from '../queryLogsApi';
import { logDataModels } from '../dataModelsLogger';
import { DialogShell, primaryBtn, surfaces } from './dataModelsUi';

const KIND_LABEL: Record<string, string> = {
  preview: 'Preview',
  query: 'Trino query',
  governed: 'Governed query',
  file: 'File query',
};

const OUTCOME_LABEL: Record<string, string> = {
  succeeded: 'Succeeded',
  denied: 'Denied',
  failed: 'Failed',
};

const OUTCOME_COLOR: Record<string, { light: string; dark: string }> = {
  succeeded: { light: '#059669', dark: '#34d399' },
  denied: { light: '#e11d48', dark: '#fb7185' },
  failed: { light: '#64748b', dark: '#94a3b8' },
};

const SERIES = { light: '#1B6CA8', dark: '#38bdf8' };
const KIND_COLORS_LIGHT = ['#1B6CA8', '#7c3aed', '#0f766e', '#d97706', '#e11d48', '#64748b'];
const KIND_COLORS_DARK = ['#38bdf8', '#a78bfa', '#2dd4bf', '#fbbf24', '#fb7185', '#94a3b8'];

function chartCard(isLight: boolean): string {
  return `rounded-xl border p-4 ${isLight ? 'border-[#E6E8EB] bg-white' : 'border-white/10 bg-black'}`;
}

function formatHits(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
}

function formatDuration(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms} ms`;
}

function shortLabel(value: string, limit = 16): string {
  return value.length > limit ? `${value.slice(0, limit - 1)}…` : value;
}

function dayLabel(key: string): string {
  const stamp = new Date(`${key}T00:00:00Z`);
  if (Number.isNaN(stamp.getTime())) return key;
  return stamp.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ChartTooltip({
  active,
  payload,
  label,
  isLight,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string; payload?: { label?: string } }>;
  label?: string;
  isLight: boolean;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className={`rounded-lg px-3 py-2 text-[12px] shadow-lg ${
        isLight ? 'border border-[#E6E8EB] bg-white text-[#3C4048]' : 'border border-white/10 bg-[#111] text-slate-200'
      }`}
    >
      {label ? <p className={`mb-1 font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{label}</p> : null}
      {payload.map((item) => (
        <p key={item.name} className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: item.color }} />
          <span>{item.name}</span>
          <span className="tabular-nums font-medium">{formatHits(Number(item.value || 0))}</span>
        </p>
      ))}
    </div>
  );
}

export default function QueryLogsMetricsDialog({
  isLight,
  scopeLabel,
  query,
  onClose,
}: {
  isLight: boolean;
  scopeLabel: string;
  query: QueryLogQuery;
  onClose: () => void;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const [metrics, setMetrics] = useState<QueryLogMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const series = isLight ? SERIES.light : SERIES.dark;
  const kindColors = isLight ? KIND_COLORS_LIGHT : KIND_COLORS_DARK;
  const axis = isLight ? '#9AA1AB' : '#64748b';
  const grid = isLight ? '#EEF1F4' : 'rgba(255,255,255,0.06)';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    logDataModels('app', 'workspace_query_logs_metrics_opened', {
      workspaceId: query.workspaceId,
      catalogId: query.catalogId,
      assetId: query.assetId,
    });
    fetchWorkspaceQueryLogMetrics(query)
      .then((next) => {
        if (!cancelled) setMetrics(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load query metrics');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const days = useMemo(
    () => (metrics?.byDay ?? []).map((item) => ({ ...item, label: dayLabel(item.key), hits: item.hits })),
    [metrics],
  );
  const outcomes = useMemo(
    () =>
      (metrics?.byOutcome ?? []).map((item) => ({
        ...item,
        label: OUTCOME_LABEL[item.key] || item.label || item.key,
        color: (OUTCOME_COLOR[item.key] || OUTCOME_COLOR.failed)[isLight ? 'light' : 'dark'],
      })),
    [isLight, metrics],
  );
  const kinds = useMemo(
    () =>
      (metrics?.byKind ?? []).map((item, index) => ({
        ...item,
        label: KIND_LABEL[item.key] || item.label || item.key,
        color: kindColors[index % kindColors.length],
      })),
    [kindColors, metrics],
  );
  const users = useMemo(() => labeledBars(metrics?.byUser), [metrics]);
  const assets = useMemo(() => labeledBars(metrics?.byAsset), [metrics]);

  return (
    <DialogShell
      title="Query metrics"
      subtitle={`Hits, users, assets, and outcomes for ${scopeLabel}.`}
      isLight={isLight}
      size="full"
      onClose={onClose}
      footer={
        <button type="button" className={primaryBtn()} onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="max-h-[calc(100vh-9rem)] overflow-y-auto px-4 py-4">
        {loading ? <p className={`py-10 text-sm ${muted}`}>Loading metrics…</p> : null}
        {error ? <p className={`text-sm ${isLight ? 'text-red-600' : 'text-red-300'}`}>{error}</p> : null}
        {!loading && metrics ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Kpi isLight={isLight} label="Total hits" value={formatHits(metrics.hits)} hint={`${metrics.succeeded} succeeded`} />
              <Kpi isLight={isLight} label="Users" value={formatHits(metrics.users)} hint="Distinct actors" />
              <Kpi isLight={isLight} label="Assets" value={formatHits(metrics.assets)} hint="Distinct assets queried" />
              <Kpi isLight={isLight} label="Avg duration" value={formatDuration(metrics.avgDurationMs)} hint={`${metrics.denied} denied · ${metrics.failed} failed`} />
            </div>

            <section className={chartCard(isLight)}>
              <Header isLight={isLight} title="Hits over time" hint="Daily volume in the selected range" />
              {days.length ? (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={days} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="queryHitsFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={series} stopOpacity={0.28} />
                          <stop offset="100%" stopColor={series} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={grid} vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
                      <Tooltip content={<ChartTooltip isLight={isLight} />} />
                      <Area type="monotone" dataKey="hits" name="Hits" stroke={series} strokeWidth={2} fill="url(#queryHitsFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty hint="No hits in this range." muted={muted} />
              )}
            </section>

            <div className="grid gap-4 xl:grid-cols-2">
              <DonutCard isLight={isLight} title="Outcome" hint="Succeeded, denied, and failed" data={outcomes} center={formatHits(metrics.hits)} />
              <DonutCard isLight={isLight} title="Query kind" hint="Preview, Trino, governed, and file" data={kinds} center={formatHits(metrics.hits)} />
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <RankCard isLight={isLight} title="Users" hint="Top actors by hits" data={users} color={series} empty="No user breakdown yet." />
              <RankCard isLight={isLight} title="Assets" hint="Top assets by hits" data={assets} color={isLight ? '#7c3aed' : '#a78bfa'} empty="No asset breakdown yet." />
            </div>
          </div>
        ) : null}
      </div>
    </DialogShell>
  );
}

function labeledBars(rows: QueryLogMetricPoint[] | undefined): Array<QueryLogMetricPoint & { short: string }> {
  return (rows ?? []).map((item) => ({ ...item, label: item.label || item.key, short: shortLabel(item.label || item.key) }));
}

function Header({ isLight, title, hint }: { isLight: boolean; title: string; hint: string }) {
  return (
    <div className="mb-3">
      <h3 className={`text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{title}</h3>
      <p className={`text-[12px] ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{hint}</p>
    </div>
  );
}

function Kpi({ isLight, label, value, hint }: { isLight: boolean; label: string; value: string; hint: string }) {
  return (
    <article className={chartCard(isLight)}>
      <p className={`text-[12px] ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{value}</p>
      <p className={`mt-1 text-[11px] ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{hint}</p>
    </article>
  );
}

function DonutCard({
  isLight,
  title,
  hint,
  data,
  center,
}: {
  isLight: boolean;
  title: string;
  hint: string;
  data: Array<QueryLogMetricPoint & { color: string }>;
  center: string;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const slices = data.filter((item) => item.hits > 0);
  const total = data.reduce((sum, item) => sum + item.hits, 0) || 1;
  return (
    <section className={chartCard(isLight)}>
      <Header isLight={isLight} title={title} hint={hint} />
      {slices.length ? (
        <>
          <div className="relative h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={slices} dataKey="hits" nameKey="label" innerRadius={52} outerRadius={74} paddingAngle={2} stroke="none">
                  {slices.map((item) => (
                    <Cell key={item.key} fill={item.color} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip isLight={isLight} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <p className={`text-xl font-semibold tabular-nums ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{center}</p>
              <p className={`text-[11px] ${muted}`}>hits</p>
            </div>
          </div>
          <ul className="mt-3 space-y-2">
            {data.map((item) => (
              <li key={item.key} className="flex items-center justify-between gap-3 text-[13px]">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: item.color }} />
                  <span className={`truncate ${isLight ? 'text-[#1C1C1C]' : 'text-slate-100'}`}>{item.label}</span>
                </span>
                <span className={`shrink-0 tabular-nums ${muted}`}>
                  {formatHits(item.hits)} · {Math.round((item.hits / total) * 100)}%
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <Empty hint="No breakdown yet." muted={muted} />
      )}
    </section>
  );
}

function RankCard({
  isLight,
  title,
  hint,
  data,
  color,
  empty,
}: {
  isLight: boolean;
  title: string;
  hint: string;
  data: Array<QueryLogMetricPoint & { short: string }>;
  color: string;
  empty: string;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const axis = isLight ? '#9AA1AB' : '#64748b';
  const grid = isLight ? '#EEF1F4' : 'rgba(255,255,255,0.06)';
  return (
    <section className={chartCard(isLight)}>
      <Header isLight={isLight} title={title} hint={hint} />
      {data.length ? (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 0 }}>
              <CartesianGrid stroke={grid} horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="short" width={92} tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip isLight={isLight} />} />
              <Bar dataKey="hits" name="Hits" fill={color} radius={[0, 4, 4, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Empty hint={empty} muted={muted} />
      )}
    </section>
  );
}

function Empty({ hint, muted }: { hint: string; muted: string }) {
  return <p className={`py-10 text-sm ${muted}`}>{hint}</p>;
}
